// src/engine/scoring.ts

import { Question, UserAnswer, CategoryScore, AssessmentResult, RiskLevel, UnknownAnswer } from '../types';
import { detectCriticalGaps } from './gapAnalysis';
import { generateRoadmap, generateRoadmapMetadata } from '../data/roadmaps';

export function calculateScore(
  questions: Question[],
  answers: UserAnswer[]
): AssessmentResult {
  const answerMap = new Map(answers.map(a => [a.questionId, a.value]));

  // Track unknown answers
  const unknownAnswers: UnknownAnswer[] = [];

  // Category weights (total = 100 points)
  const categoryWeights = {
    privacy: 15,
    admin: 25,
    physical: 10,
    technical: 25,
    breach: 10,
    ba: 10,
    ai: 10, // conditional
    updates: 5,
  };

  const categoryScores: CategoryScore[] = [];
  let totalScore = 0;
  let maxScore = 0;

  // Calculate score for each category
  Object.keys(categoryWeights).forEach((category) => {
    const categoryQuestions = questions.filter(q => q.section === category);
    const { score, max, unknowns } = scoreCategoryQuestions(categoryQuestions, answerMap, questions);

    // Collect unknown answers
    unknowns.forEach(u => unknownAnswers.push(u));

    // Only include if user answered questions in this category
    if (max > 0) {
      const percentage = (score / max) * 100;
      const weightedScore = (percentage / 100) * categoryWeights[category as keyof typeof categoryWeights];

      categoryScores.push({
        category: getCategoryName(category),
        score: Math.round(score),
        maxScore: Math.round(max),
        percentage: Math.round(percentage),
        status: percentage >= 80 ? 'good' : percentage >= 50 ? 'needs-work' : 'critical',
      });

      totalScore += weightedScore;
      maxScore += categoryWeights[category as keyof typeof categoryWeights];
    }
  });

  const finalPercentage = (totalScore / maxScore) * 100;
  const riskLevel = getRiskLevel(finalPercentage);

  // Detect critical gaps
  const criticalGaps = detectCriticalGaps(questions, answers);

  // Get organization size from answers
  const orgSize = getOrganizationSizeFromAnswers(answers);

  // Identify strengths (categories scoring 80%+)
  const strengths = categoryScores
    .filter(c => c.percentage >= 80)
    .map(c => ({
      title: `Strong ${c.category}`,
      description: `Your organization scores ${c.percentage}% in ${c.category}`,
    }));

  // Generate personalized roadmap based on org size
  const roadmap = generateRoadmap(criticalGaps, categoryScores, riskLevel, orgSize);
  const roadmapMetadata = generateRoadmapMetadata(orgSize);

  // Calculate potential score improvement if unknowns are resolved
  const scoreIfUnknownsResolved = unknownAnswers.length > 0 
    ? calculateImprovedScore(finalPercentage, unknownAnswers.length, maxScore)
    : undefined;

  return {
    totalScore: Math.round(totalScore),
    maxScore: 100,
    percentage: Math.round(finalPercentage),
    riskLevel,
    categoryScores,
    criticalGaps,
    strengths,
    roadmap,
    projectedImprovement: {
      currentScore: Math.round(finalPercentage),
      projectedScore: Math.min(100, Math.round(finalPercentage + 20)),
      timeframe: `${roadmapMetadata.totalWeeks} weeks`,
    },
    // Unknown answer tracking
    unknownAnswers,
    hasUnknowns: unknownAnswers.length > 0,
    unknownCount: unknownAnswers.length,
    scoreIfUnknownsResolved,
    // Roadmap timeline info
    roadmapWeeks: roadmapMetadata.totalWeeks,
    roadmapPhases: roadmapMetadata.totalPhases,
    organizationSize: orgSize,
    roadmapMetadata,
  };
}

function getOrganizationSizeFromAnswers(answers: UserAnswer[]): 'small' | 'medium' | 'large' {
  const sizeAnswer = answers.find(a => a.questionId === 'Q2');
  if (!sizeAnswer) return 'small';
  const val = sizeAnswer.value as string;
  if (val === 'medium') return 'medium';
  if (val === 'large') return 'large';
  return 'small';
}

function scoreCategoryQuestions(
  questions: Question[],
  answerMap: Map<string, string | string[]>,
  allQuestions: Question[]
): { score: number; max: number; unknowns: UnknownAnswer[] } {
  let score = 0;
  let max = 0;
  const unknowns: UnknownAnswer[] = [];

  questions.forEach((question) => {
    const answer = answerMap.get(question.id);
    if (!answer) return; // Skip unanswered questions

    const maxPoints = question.weight * 3; // Max 3 points per answer, weighted
    max += maxPoints;

    if (question.type === 'single') {
      const selectedOption = question.options.find(o => o.value === answer);
      
      // Check if answer is "unknown"
      if (selectedOption?.isUnknown) {
        unknowns.push({
          questionId: question.id,
          questionText: question.question,
          section: question.section,
          investigationGuide: question.investigationGuide || {
            whoToAsk: [],
            whereToLook: [],
            howToVerify: '',
            whatToDocument: ''
          },
          priority: determineUnknownPriority(question)
        });
        // "I don't know" scores 0 points
        return;
      }
      
      if (selectedOption) {
        score += selectedOption.points * question.weight;
      }
    } else if (question.type === 'multiple' && Array.isArray(answer)) {
      // For multiple choice, average the points
      const totalPoints = answer.reduce((sum, val) => {
        const option = question.options.find(o => o.value === val);
        return sum + (option?.points || 0);
      }, 0);
      score += (totalPoints / answer.length) * question.weight;
    }
  });

  return { score, max, unknowns };
}

function getRiskLevel(percentage: number): RiskLevel {
  if (percentage >= 90) return 'LOW';
  if (percentage >= 75) return 'MODERATE';
  if (percentage >= 50) return 'HIGH';
  return 'CRITICAL';
}

function getCategoryName(category: string): string {
  const names: Record<string, string> = {
    privacy: 'Privacy Rule',
    admin: 'Administrative Safeguards',
    physical: 'Physical Safeguards',
    technical: 'Technical Safeguards',
    breach: 'Breach Preparedness',
    ba: 'Business Associates',
    ai: 'AI/ML Compliance',
    updates: '2026 Updates',
  };
  return names[category] || category;
}

// Calculate potential score improvement if unknowns are resolved
function calculateImprovedScore(
  currentPercentage: number,
  unknownCount: number,
  maxScore: number
): {
  score: number;
  percentage: number;
  improvement: number;
  estimatedWeeks: number;
} {
  // Assume average unknown answer would score 50% if resolved
  const potentialImprovement = (unknownCount * 1.5) / (maxScore / 100);
  const newPercentage = Math.min(100, currentPercentage + potentialImprovement);

  // Estimate weeks based on number of unknowns
  let estimatedWeeks = 2;
  if (unknownCount > 5) estimatedWeeks = 4;
  if (unknownCount > 10) estimatedWeeks = 6;
  if (unknownCount > 15) estimatedWeeks = 8;

  return {
    score: Math.round((newPercentage / 100) * 100),
    percentage: Math.round(newPercentage),
    improvement: Math.round(newPercentage - currentPercentage),
    estimatedWeeks,
  };
}

// Determine priority based on question weight and section
function determineUnknownPriority(question: Question): 'HIGH' | 'MEDIUM' | 'LOW' {
  const criticalSections = ['admin', 'technical', 'breach'];

  if (criticalSections.includes(question.section) && question.weight >= 2) {
    return 'HIGH';
  }
  if (question.weight >= 2) {
    return 'MEDIUM';
  }
  return 'LOW';
}
